import { ListItemIcon, Menu, MenuItem } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsisVertical,
    faFolderOpen,
    faGift,
    faTrash,
    faTrashCanArrowUp,
    faWarehouse
} from "@fortawesome/sharp-duotone-light-svg-icons";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import React, { useState } from "react";
import {
    useArchiveTripMutation,
    useDeleteTripMutation,
    useReassignTripMutation,
    useRestoreTripMutation,
    useUnarchiveTripMutation
} from "../../services/routing";
import { Trip } from "../../generated/gpb/api/route";
import useIdentity from "../../app/auth";
import { useUserViewOverride } from "../../features/riskViewer/userViewOverrideSlice";
import ReassignConfirmationModal from "./ReassignConfirmationModal";
import { ARCHIVE_TRIP, DELETE_TRIP, REASSIGN_TRIP, RESTORE_TRIP, UNARCHIVE_TRIP } from "../UserVisibleStrings";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

type RowActionsProps = {
    trip: Trip,
};


export default function RowActions( { trip }: Readonly<RowActionsProps> )
{
    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );
    const [ isReassignModalOpen, setIsReassignModalOpen ] = useState( false );
    const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
    const [ deleteTrip ] = useDeleteTripMutation();
    const [ reassignTrip ] = useReassignTripMutation();

    const [ anchorEl, setAnchorEl ] = React.useState<null | HTMLElement>( null );

    const handleClick = ( event : React.MouseEvent<HTMLButtonElement> ) =>
    {
        if ( event === undefined ) return;
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl( event.currentTarget );
    };

    const handleClose = ( event : React.MouseEvent ) =>
    {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl( null );
    };
    const handleDeleteTrip = async () =>
    {
        if ( trip.Id !== undefined )
        {
            await deleteTrip( trip.Id.hexString );
            setIsDeleteModalOpen( false ); // Close modal after deletion
        }
    };
    const wrapMenuItem = ( icon: JSX.Element, label: string, onClick: ( event: React.MouseEvent ) => void )=>
    {
        return (
            <MenuItem onClick = {( event: React.MouseEvent ) =>
            {
                handleClose( event );
                onClick( event );
            }}
            >
                <ListItemIcon>
                    {icon}
                </ListItemIcon>
                <Typography variant = "inherit">{label}</Typography>
            </MenuItem>
        );
    };
    const handleReassignTrip = async ( userId: string ) =>
    {
        if ( userId !== undefined && trip.Id !== undefined )
        {
            await reassignTrip( { tripId: trip.Id.hexString, userId: userId } );
            setIsReassignModalOpen( false ); // Close modal after reassign
        }
    };

    const [ restoreTripMutation ] = useRestoreTripMutation();
    const [ archiveTripMutation ] = useArchiveTripMutation();
    const [ unarchiveTripMutation ] = useUnarchiveTripMutation();

    const isDeleted: boolean = trip.hidden;

    function getDeleteRestoreButton( )
    {
        if ( isDeleted )
        {
            if ( !isAdmin )
            {
                return undefined; // non-admins cannot restore trips - they also shouldn't display the card in the first place...
            }
            return wrapMenuItem( <FontAwesomeIcon icon = {faTrashCanArrowUp} size = "sm" fixedWidth/>,
                RESTORE_TRIP,
                () =>
                {
                    if ( trip?.Id === undefined ) return;
                    try
                    {
                        restoreTripMutation( trip.Id.hexString ).catch( err => console.error( err ) );
                    }
                    catch ( err )
                    {
                        console.error( "Failed to " + RESTORE_TRIP.toLowerCase() + ": ", err );
                    }
                } );
        }
        return wrapMenuItem( <FontAwesomeIcon icon = {faTrash} size = "sm" fixedWidth/>,
            DELETE_TRIP,
            () =>
            {
                setIsDeleteModalOpen( true );
            } );
    }
    const tripDeleteRestore = getDeleteRestoreButton( );
    function getArchiveButton()
    {
        if ( trip === undefined ) return undefined;
        const isArchived: boolean = trip.archived;
        if ( isArchived )
        {
            return wrapMenuItem( <FontAwesomeIcon icon = {faFolderOpen} size = "sm" fixedWidth/>,
                UNARCHIVE_TRIP,
                () =>
                {
                    if ( trip?.Id !== undefined )
                    {
                        unarchiveTripMutation( trip.Id.hexString ).catch( err => console.error( err ) );
                    }
                } );
        }
        return wrapMenuItem( <FontAwesomeIcon icon = {faWarehouse} size = "sm" fixedWidth/>,
            ARCHIVE_TRIP,
            () =>
            {
                if ( trip?.Id !== undefined )
                {
                    archiveTripMutation( trip.Id.hexString ).catch( err => console.error( err ) );
                }
            } );
    }
    const reassignButton = wrapMenuItem( <FontAwesomeIcon icon = {faGift} size = "sm" fixedWidth/>,
        REASSIGN_TRIP,
        () =>
        {
            setIsReassignModalOpen( true );
        } );
    const archiveOrRestoreIcon = getArchiveButton();
    return (
        <React.Fragment>
            <Button aria-controls = "simple-menu" aria-haspopup = "true" onClick = {handleClick}>
                <FontAwesomeIcon icon = {faEllipsisVertical} size = "sm" fixedWidth/>
            </Button>
            <Menu id = "card-actions-menu" anchorEl = {anchorEl}
                keepMounted open = {Boolean( anchorEl )} onClose = {handleClose}
            >
                {isAdmin && !isDeleted && reassignButton}
                {tripDeleteRestore}
                {archiveOrRestoreIcon}
            </Menu>
            <DeleteConfirmationModal
                open = {isDeleteModalOpen}
                onClose = {() => setIsDeleteModalOpen( false )}
                onDelete = {handleDeleteTrip}
            />
            <ReassignConfirmationModal
                open = {isReassignModalOpen}
                currentUserId = {trip.userId?.wordpressId}
                currentTripId = {trip.Id?.hexString}
                onClose = {() => setIsReassignModalOpen( false )}
                onReassign = {handleReassignTrip}
            />
        </React.Fragment>

    );
}
